import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { User, Mail, Phone, Building, Briefcase, Tag, Globe, Linkedin, Instagram, AlertCircle, FileUp } from 'lucide-react';

const ContactModal = ({ isOpen, onClose, contact, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    company: '',
    email: '',
    phone: '',
    website: '',
    linkedin: '',
    instagram: '',
    tags: [],
    notes: '',
    priority: false
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const availableTags = [
    'team',
    'venue',
    'partner',
    'artist',
    'supplier',
    'media',
    'investor',
    'vendor',
    'agency',
    'government',
    'sponsor'
  ];

  useEffect(() => {
    if (contact) {
      setFormData({
        name: contact.name || '',
        role: contact.role || '',
        company: contact.company || '',
        email: contact.email || '',
        phone: contact.phone || '',
        website: contact.website || '',
        linkedin: contact.linkedin || '',
        instagram: contact.instagram || '',
        tags: contact.tags || [],
        notes: contact.notes || '',
        priority: contact.tags?.includes('priority') || false
      });
    } else {
      // Reset form for new contact
      setFormData({
        name: '',
        role: '',
        company: '',
        email: '',
        phone: '',
        website: '',
        linkedin: '',
        instagram: '',
        tags: [],
        notes: '',
        priority: false
      });
    }
    setErrors({});
  }, [contact]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTagToggle = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setLoading(true);
    try {
      // Add priority tag if checked
      const tags = [...formData.tags];
      if (formData.priority && !tags.includes('priority')) {
        tags.push('priority');
      } else if (!formData.priority) {
        const index = tags.indexOf('priority');
        if (index > -1) tags.splice(index, 1);
      }
      
      const contactData = {
        ...formData,
        tags,
        lastContact: new Date().toISOString()
      };
      
      delete contactData.priority;
      
      await onSave(contactData);
      onClose();
    } catch (error) {
      console.error('Error saving contact:', error);
      setErrors({ submit: error.message || 'Failed to save contact' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={contact ? 'Edit Contact' : 'Add New Contact'}
      maxWidth="max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-[#888888] uppercase tracking-wider">Basic Information</h4>
          
          <div>
            <label className="block text-sm font-medium mb-2">
              <User size={16} className="inline mr-1" />
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`input-dark w-full ${errors.name ? 'border-red-500' : ''}`}
              placeholder="e.g., John Doe"
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                <Briefcase size={16} className="inline mr-1" />
                Role / Title
              </label>
              <input
                type="text"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="input-dark w-full"
                placeholder="e.g., General Manager"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                <Building size={16} className="inline mr-1" />
                Company / Organization
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="input-dark w-full"
                placeholder="e.g., LX Factory"
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-[#888888] uppercase tracking-wider">Contact Information</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                <Mail size={16} className="inline mr-1" />
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`input-dark w-full ${errors.email ? 'border-red-500' : ''}`}
                placeholder="email@example.com"
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                <Phone size={16} className="inline mr-1" />
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="input-dark w-full"
                placeholder="+351 XXX XXX XXX"
              />
            </div>
          </div>
        </div>

        {/* Social & Web */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-[#888888] uppercase tracking-wider">Social & Web</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                <Globe size={16} className="inline mr-1" />
                Website
              </label>
              <input
                type="text"
                name="website"
                value={formData.website}
                onChange={handleChange}
                className="input-dark w-full"
                placeholder="www.example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                <Linkedin size={16} className="inline mr-1" />
                LinkedIn
              </label>
              <input
                type="text"
                name="linkedin"
                value={formData.linkedin}
                onChange={handleChange}
                className="input-dark w-full"
                placeholder="linkedin.com/in/username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                <Instagram size={16} className="inline mr-1" />
                Instagram
              </label>
              <input
                type="text"
                name="instagram"
                value={formData.instagram}
                onChange={handleChange}
                className="input-dark w-full"
                placeholder="@username"
              />
            </div>
          </div>
        </div>

        {/* Tags & Priority */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-[#888888] uppercase tracking-wider">
            <Tag size={16} className="inline mr-1" />
            Tags & Priority
          </h4>
          
          <div className="flex items-center gap-2 mb-4">
            <input
              type="checkbox"
              id="priority"
              name="priority"
              checked={formData.priority}
              onChange={handleChange}
              className="rounded border-[#333333] bg-[#0A0A0A] text-[#D4A574] focus:ring-[#D4A574]"
            />
            <label htmlFor="priority" className="text-sm flex items-center gap-2">
              Mark as Priority Contact
              <span className="text-[#D4A574]">★</span>
            </label>
          </div>
          
          <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
            {availableTags.map(tag => (
              <button
                key={tag}
                type="button"
                onClick={() => handleTagToggle(tag)}
                className={`px-3 py-2 rounded-lg text-sm capitalize transition-colors ${
                  formData.tags.includes(tag)
                    ? 'bg-[#D4A574] text-black'
                    : 'bg-[#0A0A0A] hover:bg-[#111111]'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-[#888888] uppercase tracking-wider">Additional Notes</h4>
          
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={4}
            className="input-dark w-full"
            placeholder="Any additional information about this contact..."
          />
        </div>

        {/* Error Message */}
        {errors.submit && (
          <div className="p-4 bg-red-500/10 border border-red-500 rounded-lg flex items-center gap-2">
            <AlertCircle size={20} className="text-red-500" />
            <p className="text-red-500 text-sm">{errors.submit}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end pt-4 border-t border-[#333333]">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : contact ? 'Save Changes' : 'Add Contact'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ContactModal;